import React, { useState } from 'react';
import { Box, TextField, Tooltip, IconButton, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AddIcon from '@mui/icons-material/Add';
import { PRIMARY, PRIMARY_DARK, ALLOWED_ATTACHMENT_LABEL, AttachmentChip, EditorialAreaChip } from './sharedUi';
import ContextSummaryChip from './ContextSummaryChip';

/**
 * Composer único do chat (estilo ChatGPT): caixa flutuante com bordas
 * arredondadas, textarea sem borda interna e barra de ações embutida na
 * própria caixa (anexos / área editorial / contexto à esquerda, enviar à
 * direita). No mobile, as 3 ações colapsam num botão "+" com menu.
 *
 * @param {{
 *   instruction: string;
 *   onInstructionChange: (value: string) => void;
 *   onKeyDown: (e: React.KeyboardEvent) => void;
 *   disabled: boolean;
 *   placeholder: string;
 *   textareaRef: React.Ref;
 *   attachments: File[];
 *   onRemoveAttachment: (index: number) => void;
 *   onOpenFilePicker: () => void;
 *   editorialAreas: object[] | null;
 *   selectedEditorialArea: object | null;
 *   onOpenEditorialAreaModal: () => void;
 *   onRemoveEditorialArea: () => void;
 *   contextSources: Array<{ key: string; label: string; icon?: React.ReactNode; selectedCount: number; summaryItems?: string[] }>;
 *   onOpenContextModal: (key?: string) => void;
 *   onClearContextGroup: (key: string) => void;
 *   onSend: () => void;
 *   sendDisabled: boolean;
 *   sendLabel: string;
 *   sendIconOnly?: boolean;
 *   sendIcon: React.ReactNode;
 *   sendTooltip: string;
 *   isMobile: boolean;
 * }} props
 */
export default function Composer({
    instruction, onInstructionChange, onKeyDown, disabled, placeholder, textareaRef,
    attachments = [], onRemoveAttachment, onOpenFilePicker,
    editorialAreas = null, selectedEditorialArea = null, onOpenEditorialAreaModal, onRemoveEditorialArea,
    contextSources = [], onOpenContextModal, onClearContextGroup,
    onSend, sendDisabled, sendLabel, sendIconOnly = false, sendIcon, sendTooltip,
    isMobile = false,
}) {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const showEditorialAreaAction = editorialAreas != null;
    const showContextAction = contextSources.length > 0;
    const contextTotalSelected = contextSources.reduce((sum, s) => sum + (s.selectedCount || 0), 0);

    const hasChips = Boolean(selectedEditorialArea)
        || attachments.length > 0
        || contextSources.some((s) => s.selectedCount > 0);

    const handleOpenMenu = (e) => setMenuAnchor(e.currentTarget);
    const handleCloseMenu = () => setMenuAnchor(null);

    const actionButtonSx = {
        color: '#666',
        width: 34,
        height: 34,
        '&:hover': { color: PRIMARY, backgroundColor: `${PRIMARY}0c` },
    };

    const sendButtonSx = {
        backgroundColor: PRIMARY,
        color: '#fff',
        width: sendIconOnly ? 36 : 'auto',
        height: 36,
        minWidth: sendIconOnly ? 36 : undefined,
        px: sendIconOnly ? 0 : 1.5,
        borderRadius: '18px',
        boxShadow: 'none',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.88rem',
        '&:hover': { backgroundColor: PRIMARY_DARK, boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: '#f5c7b5', color: '#fff' },
    };

    return (
        <Box sx={{
            borderRadius: '24px',
            border: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            p: '10px 12px 8px',
        }}>
            {hasChips && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 0.5, px: 0.5 }}>
                    {selectedEditorialArea && (
                        <EditorialAreaChip
                            name={selectedEditorialArea.name}
                            onRemove={disabled ? null : onRemoveEditorialArea}
                        />
                    )}
                    {attachments.map((file, idx) => (
                        <AttachmentChip
                            key={`${file.name}-${file.size}-${file.lastModified}`}
                            attachment={file}
                            onRemove={disabled ? null : () => onRemoveAttachment(idx)}
                        />
                    ))}
                    {contextSources.filter((source) => source.selectedCount > 0).map((source) => (
                        <ContextSummaryChip
                            key={source.key}
                            icon={source.icon}
                            label={source.label}
                            count={source.selectedCount}
                            items={source.summaryItems}
                            onClick={() => onOpenContextModal(source.key)}
                            onClear={disabled ? null : () => onClearContextGroup(source.key)}
                        />
                    ))}
                </Box>
            )}

            <TextField
                inputRef={textareaRef}
                multiline
                minRows={1}
                maxRows={8}
                fullWidth
                variant="standard"
                placeholder={placeholder}
                value={instruction}
                onChange={(e) => onInstructionChange(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={disabled}
                InputProps={{ disableUnderline: true }}
                sx={{
                    px: 0.5,
                    '& .MuiInputBase-root': {
                        fontSize: '0.95rem',
                        lineHeight: 1.5,
                        color: '#333',
                    },
                    '& .MuiInputBase-input': {
                        py: 0.5,
                        '&::placeholder': { color: '#aaa', opacity: 1 },
                    },
                }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25, px: 0.25 }}>
                {isMobile ? (
                    <>
                        <Tooltip title="Mais opções">
                            <span>
                                <IconButton
                                    onClick={handleOpenMenu}
                                    disabled={disabled}
                                    aria-label="Mais opções"
                                    sx={actionButtonSx}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
                            <MenuItem onClick={() => { handleCloseMenu(); onOpenFilePicker(); }}>
                                <ListItemIcon><AttachFileIcon fontSize="small" sx={{ color: '#666' }} /></ListItemIcon>
                                <ListItemText>Anexar arquivo</ListItemText>
                            </MenuItem>
                            {showEditorialAreaAction && (
                                <MenuItem onClick={() => { handleCloseMenu(); onOpenEditorialAreaModal(); }}>
                                    <ListItemIcon><StyleOutlinedIcon fontSize="small" sx={{ color: '#666' }} /></ListItemIcon>
                                    <ListItemText>Área editorial</ListItemText>
                                </MenuItem>
                            )}
                            {showContextAction && (
                                <MenuItem onClick={() => { handleCloseMenu(); onOpenContextModal(); }}>
                                    <ListItemIcon><TravelExploreIcon fontSize="small" sx={{ color: '#666' }} /></ListItemIcon>
                                    <ListItemText>
                                        {contextTotalSelected > 0
                                            ? `Adicionar contexto (${contextTotalSelected})`
                                            : 'Adicionar contexto'}
                                    </ListItemText>
                                </MenuItem>
                            )}
                        </Menu>
                    </>
                ) : (
                    <>
                        <Tooltip title={disabled ? 'Aguarde a geração terminar' : `Anexar arquivo — formatos aceitos: ${ALLOWED_ATTACHMENT_LABEL}`}>
                            <span>
                                <IconButton
                                    onClick={onOpenFilePicker}
                                    disabled={disabled}
                                    aria-label="Anexar arquivo"
                                    sx={actionButtonSx}
                                >
                                    <AttachFileIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {showEditorialAreaAction && (
                            <Tooltip title={disabled ? 'Aguarde a geração terminar' : 'Definir o tom de escrita a partir de uma área editorial'}>
                                <span>
                                    <IconButton
                                        onClick={onOpenEditorialAreaModal}
                                        disabled={disabled}
                                        aria-label="Área editorial"
                                        sx={actionButtonSx}
                                    >
                                        <StyleOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}

                        {showContextAction && (
                            <Tooltip title="Adicionar contexto (sugestões NeoFeed / notícias relacionadas)">
                                <span>
                                    <IconButton
                                        onClick={() => onOpenContextModal()}
                                        aria-label="Adicionar contexto"
                                        sx={actionButtonSx}
                                    >
                                        <TravelExploreIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </>
                )}

                <Box sx={{ flex: 1 }} />

                <Tooltip title={sendTooltip} placement="top">
                    <span>
                        {sendIconOnly ? (
                            <IconButton
                                onClick={onSend}
                                disabled={sendDisabled}
                                aria-label={sendLabel}
                                sx={sendButtonSx}
                            >
                                {sendIcon}
                            </IconButton>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={onSend}
                                disabled={sendDisabled}
                                startIcon={sendIcon}
                                sx={sendButtonSx}
                            >
                                {sendLabel}
                            </Button>
                        )}
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
}
